import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  brandFonts,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  visuallyHidden,
} from '../styles';
import { Container, Link, Svg } from './ui';
import instagramIcon from '../images/instagram-icon.inline.svg';
import facebookIcon from '../images/facebook-icon.inline.svg';
import twitterIcon from '../images/twitter-icon.inline.svg';
import linkedInIcon from '../images/linkedIn-icon.inline.svg';
import pinterestIcon from '../images/pinterest-icon.inline.svg';

const StyledFooter = styled.footer`
  ${maxBreakpointQuery.mlarge`
    text-align: center;
  `}
`;

const StyledTop = styled.div`
  background-color: ${brandColours.secondary};
  padding: 65px 0 80px;

  ${minBreakpointQuery.small`
    padding: 75px 0 90px;
  `}

  ${minBreakpointQuery.mlarge`
    padding: 85px 0 100px;
  `}

  ${minBreakpointQuery.large`
    padding: 95px 0 120px;
  `}
`;

const StyledWrapper = styled.div`
  ${minBreakpointQuery.small`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 50px;
    row-gap: 40px;
  `}

  ${minBreakpointQuery.mlarge`
    grid-template-columns: repeat(3, 1fr);
  `}
`;

const StyledGroup = styled.div`
  color: ${standardColours.darkerGrey};
  ${fontSize(14)};

  &:last-child {
    margin-bottom: 0;

    ${minBreakpointQuery.small`
      grid-column: 1 / 3;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      column-gap: 50px;
      ${fontSize(16)};
    `}

    ${minBreakpointQuery.mlarge`
      grid-column: 3 / 4;
      display: block;
      grid-template-columns: unset;
      column-gap: unset;
      ${fontSize(18)};
    `}
  }

  ${maxBreakpointQuery.small`
    margin-bottom: 40px;
  `}
`;

const StyledHeading = styled.h3`
  font-family: ${brandFonts.secondary};
  font-weight: ${fontWeights.regular};
  ${fontSize(18)}
  color: ${standardColours.black};
  line-height: 1.3;

  ${minBreakpointQuery.small`
    ${fontSize(20)}
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(22)}
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)}
  `}
`;

const StyledList = styled.ul`
  margin-top: 25px;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 50px;
  `}
`;

const StyledListItem = styled.li`
  margin: 15px 0;

  ${minBreakpointQuery.small`
    margin: 10px 0;
    line-height: 2;
  `}

  ${minBreakpointQuery.mlarge`
    margin: 15px 0;
  `}
`;

const StyledListLink = styled(Link)`
  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(18)};
  `}
`;

const StyledContact = styled.div`
  line-height: 2;
`;

const StyledAddress = styled.p`
  white-space: break-spaces;
  margin-top: 25px;
  margin-bottom: 25px;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 50px;
  `}
`;

const StyledSocial = styled.div`
  ${maxBreakpointQuery.small`
    margin-top: 60px;
  `};

  ${minBreakpointQuery.mlarge`
    margin-top: 50px;
  `}
`;

const StyledSocialIcons = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: 40px auto 0;
  max-width: 230px;

  ${minBreakpointQuery.mlarge`
    margin: 40px 0 0;
    justify-content: space-between;
    max-width: 280px;
  `}
`;

const StyledSocialLink = styled.a`
  span {
    ${visuallyHidden()};
  }
`;

const StyledIcon = styled(Svg)`
  width: 35px;
  height: 35px;

  ${minBreakpointQuery.mlarge`
    width: 45px;
    height: 45px;
  `}
`;

const StyledBottom = styled.div`
  background-color: ${brandColours.primary};
  color: ${standardColours.black};
  ${fontSize(12)}
  padding: 25px 0;
  text-align: center;

  ${minBreakpointQuery.mlarge`
    ${fontSize(14)}
  `}
`;

const StyledBottomInner = styled.div`
  display: flex;
  gap: 10px;

  ${maxBreakpointQuery.xlarge`
    flex-direction: column;
  `}

  ${minBreakpointQuery.xlarge`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `}

  a {
    text-decoration: underline;
  }
`;

const Footer = () => {
  const {
    datoCmsFooter: {
      helpInformationLinks,
      shopLinks,
      copyrightText,
      legalLinks,
    },
    datoCmsContactInformation: {
      address,
      email,
      phone,
      instagramProfile,
      facebookProfile,
      twitterProfile,
      linkedinProfile,
      pinterestProfile,
    },
  } = useStaticQuery(graphql`
    query FooterQuery {
      datoCmsFooter {
        helpInformationLinks: navigation {
          ... on DatoCmsLink {
            id
            text
            page {
              ...LinkFragment
            }
          }
          ... on DatoCmsExternalLink {
            id
            text
            url
          }
        }
        shopLinks {
          ... on DatoCmsLink {
            id
            text
            page {
              ...LinkFragment
            }
          }
          ... on DatoCmsExternalLink {
            id
            text
            url
          }
        }
        copyrightText
        legalLinks {
          id
          text
          page {
            ...LinkFragment
          }
        }
      }
      datoCmsContactInformation {
        address
        email
        phone
        instagramProfile
        facebookProfile
        twitterProfile
        linkedinProfile
        pinterestProfile
      }
      allDatoCmsProductCategory(
        filter: { root: { eq: true } }
        sort: { fields: position, order: ASC }
      ) {
        nodes {
          title
          shopifyCollection
        }
      }
    }
  `);

  return (
    <StyledFooter>
      <StyledTop>
        <Container>
          <StyledWrapper>
            <StyledGroup>
              <StyledHeading>Help &amp; Information</StyledHeading>
              <StyledList>
                {helpInformationLinks.map(({ id, text, page, url }) => (
                  <StyledListItem key={id}>
                    <StyledListLink href={url && url} to={page && page}>
                      {text}
                    </StyledListLink>
                  </StyledListItem>
                ))}
              </StyledList>
            </StyledGroup>
            <StyledGroup>
              <StyledHeading>Shop</StyledHeading>
              <StyledList>
                {shopLinks.map(({ id, text, page, url }) => (
                  <StyledListItem key={id}>
                    <StyledListLink href={url && url} to={page && page}>
                      {text}
                    </StyledListLink>
                  </StyledListItem>
                ))}
              </StyledList>
            </StyledGroup>
            <StyledGroup>
              <StyledContact>
                <StyledHeading>Contact Us</StyledHeading>
                <StyledAddress>{address}</StyledAddress>
                <p>{email}</p>
                <p>{phone}</p>
              </StyledContact>
              {(instagramProfile ||
                facebookProfile ||
                twitterProfile ||
                linkedinProfile ||
                pinterestProfile) && (
                <StyledSocial>
                  <StyledHeading>Stay Connected</StyledHeading>
                  <StyledSocialIcons>
                    {instagramProfile && (
                      <StyledSocialLink
                        href={instagramProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StyledIcon image={instagramIcon} />
                        <span>Instagram</span>
                      </StyledSocialLink>
                    )}
                    {facebookProfile && (
                      <StyledSocialLink
                        href={facebookProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StyledIcon image={facebookIcon} />
                        <span>Facebook</span>
                      </StyledSocialLink>
                    )}
                    {twitterProfile && (
                      <StyledSocialLink
                        href={twitterProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StyledIcon image={twitterIcon} />
                        <span>Twitter</span>
                      </StyledSocialLink>
                    )}
                    {linkedinProfile && (
                      <StyledSocialLink
                        href={linkedinProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StyledIcon image={linkedInIcon} />
                        <span>LinkedIn</span>
                      </StyledSocialLink>
                    )}
                    {pinterestProfile && (
                      <StyledSocialLink
                        href={pinterestProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StyledIcon image={pinterestIcon} />
                        <span>Pinterest</span>
                      </StyledSocialLink>
                    )}
                  </StyledSocialIcons>
                </StyledSocial>
              )}
            </StyledGroup>
          </StyledWrapper>
        </Container>
      </StyledTop>
      <StyledBottom>
        <Container>
          <StyledBottomInner>
            <p>
              &copy; {new Date().getFullYear()} {copyrightText}
            </p>
            <p>
              {legalLinks.map(({ id, text, page }) => (
                <React.Fragment key={id}>
                  <Link to={page}>{text}</Link>
                  {' | '}
                </React.Fragment>
              ))}
              <a
                href="https://www.webrevolve.co.uk"
                target="_blank"
                rel="noopener"
              >
                Website by Webrevolve
              </a>
            </p>
          </StyledBottomInner>
        </Container>
      </StyledBottom>
    </StyledFooter>
  );
};

export default Footer;
